<script setup>
import { ref, computed, onMounted, onActivated, watch } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { usePaginacao } from '@/composables/usePaginacao'
import { useUiStore } from '@/stores/ui'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'

const emit = defineEmits(['adicionar-item', 'editar-item', 'show-notification'])

const authStore = useAuthStore()
const uiStore = useUiStore()
const { paginacao } = usePaginacao(25)

const lista = ref([])
const loading = ref(false)
const erro = ref(null)

const formatarValor = (valor) => {
  if (valor == null) return '-'
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(valor)
}

const colunas = [
  { field: 'nomeParceiro', label: 'Fornecedor', sortable: true, class: 'fw-medium' },
  { field: 'valorAlocadoPrestacaoNaoConcluida', label: 'Alocado (Não Concluída)', align: 'right', sortable: true, formatter: (v) => formatarValor(v) },
  { field: 'adiantadoPendente', label: 'Adiantado Pendente', align: 'right', sortable: true, formatter: (v) => formatarValor(v) },
  { field: 'totalTitulosAberto', label: 'Títulos em Aberto (Total)', align: 'right', sortable: true, formatter: (v) => formatarValor(v) },
  { field: 'titulosCobertosPrestacaoNaoConcluida', label: 'Coberto por Não Concluída', align: 'right', sortable: true, formatter: (v) => formatarValor(v) },
  { field: 'titulosNaoCobertos', label: 'Não Coberto', align: 'right', sortable: true, formatter: (v) => formatarValor(v) },
  { field: 'acoes', label: 'Ações', width: 120, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
]

const temPermissaoCompleta = computed(() => authStore.isAdminInterno || (authStore.permissoes && authStore.permissoes.includes('MODULO_COMPLETO')))

const buscarDados = async (pagina = 1) => {
  if (!authStore.empresaAtiva && !authStore.isAdminInterno) return

  loading.value = true
  erro.value = null
  paginacao.value.CurrentPage = pagina

  try {
    const response = await api.get('/PrestacoesContas/creditos-fornecedor')
    lista.value = response.data
    paginacao.value.TotalCount = lista.value.length
    paginacao.value.TotalPages = 1
  } catch (error) {
    erro.value = error.response?.data?.message || 'Erro ao carregar créditos por fornecedor.'
    lista.value = []
  } finally {
    loading.value = false
  }
}

const criarPrestacao = (item) => {
  emit('editar-item', {
    isNovaPrestacao: true,
    idParceiro: item.idParceiro,
    nomeParceiro: item.nomeParceiro
  })
}

watch(() => authStore.empresaAtiva, (novaEmpresa) => {
  if (novaEmpresa) {
    buscarDados(1)
  } else {
    lista.value = []
    paginacao.value.TotalCount = 0
  }
})

onMounted(async () => {
  if (authStore.empresaAtiva || !authStore.isAdminInterno) buscarDados(1)
})

onActivated(() => { buscarDados(1) })

defineExpose({ buscarDados })
</script>

<template>
  <PaginaAdministrar
    :titulo="uiStore.currentModuleTitle || 'Créditos por Fornecedor'"
    subtitulo="Créditos de Adiantamentos disponíveis e títulos em aberto por fornecedor"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    :mostrar-botao-adicionar="false"
  >
    <div v-if="loading" class="text-center p-5"><div class="spinner-border text-primary"></div></div>
    <div v-else-if="erro" class="alert alert-danger m-3 border-0 d-flex align-items-center">
      <i class="bi bi-exclamation-triangle-fill fs-4 me-3"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="lista"
      :loading="loading"
      empty-message="Nenhum fornecedor com crédito pendente."
      row-key="idParceiro"
    >
      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1 acoes-wrapper">
          <button v-if="row.adiantadoPendente > 0 && temPermissaoCompleta" class="btn-icon text-primary" @click="criarPrestacao(row)" title="Nova Prestação de Contas">
            <i class="bi bi-plus-circle-fill"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>
</template>
